import {
  FilterTypes,
  type ProductFilter,
  type ProductSearchQuery,
  type TransformedFilter,
} from '@scayle/storefront-nuxt'
import { pickBy } from 'es-toolkit/compat'
import { transformWhereConditionToMap } from '../utils/filter'

// +-----------+
// | overrides |
// +-----------+

// Custom version of Scayle's groupFilterableValuesByKey that returns filter.value instead of filter.id
export const groupFilterableValuesByKeyOverride = (
  filterableValues: ProductFilter[],
  sortable: boolean = true,
) => {
  const transformedAvailableFilters = filterableValues.reduce(
    (availableFilters: TransformedFilter[], filter) => {
      filter.values.forEach((value: any) => {
        const isBoolean = filter.type === FilterTypes.BOOLEAN
        const hasNameInValue = 'name' in value
        if (isBoolean && hasNameInValue && value.name === false) {
          return
        }
        // Modified: Use value.value instead of value.id for the filter value
        const filterValue = _getFilterValueByType(value, filter.type)
        const nameValue = hasNameInValue ? String(value.name) : ''
        availableFilters.push({
          key: filter.slug,
          displayName: isBoolean ? filter.name : nameValue,
          count: value.productCount,
          value: filterValue, // This now returns value.value instead of value.id
          whereCondition: {
            [filter.slug]: filterValue,
          },
        })
      })
      return availableFilters
    },
    [],
  )
  return sortable
    ? transformedAvailableFilters.sort((a: any, b: any) =>
        a.displayName.localeCompare(b.displayName),
      )
    : transformedAvailableFilters
}

const _getFilterValueByType = (value: any, type: FilterTypes) => {
  switch (type) {
    case FilterTypes.BOOLEAN:
      return value?.name
    case FilterTypes.RANGE:
      return { min: value?.min, max: value?.max }
    default:
      return value?.value // Changed from value.id to value.value
  }
}

/**
 * @info - Ideally, this should come directly from Odoscope
 * @description - Turns facet keys that look like this: "tamaris_1", "alba_moda_2", etc. into a more readable format
 * @example: "alba_moda_2" -> "Alba Moda"
 * @param key - The facet key to format
 * @returns The formatted display name
 */
export const formatDisplayName = (key: string): string => {
  return key
    .replace(/_\d+$/, '') // Remove trailing numbers
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (l) => l.toUpperCase()) // Capitalize first letter of each word
}

/**
 * Transforms the filters (product where Condition) to the format used by odoscope and converts those filters to query format
 * @param filters - The filters to transform
 * @returns The transformed filters
 */
export const transformFiltersToOdoscopeQuery = (
  appliedFilters: ProductSearchQuery,
) => {
  const transformed = transformWhereConditionToMap(appliedFilters)

  // Remove keys already handled by the search query builder (q, term, etc.)
  const { term, ...filtersOnly } = transformed

  // clean up transformed filters
  const result = pickBy(filtersOnly, (value) => {
    if (value == null || value === '') {
      return false
    }
    if (Array.isArray(value)) {
      return value.length > 0
    }
    return true
  })

  // convert transformed filters to query format
  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(',') : value,
    ]),
  )
}
